/**
 * Paged carousel for the Dose Review Carousel section. Desktop shows three
 * cards per page, mobile one; the dots are built client-side because the
 * page count depends on the breakpoint (ceil(cards / per-view)). The active
 * page's dot renders as a dash. Arrows page the scroll; dragging the track
 * updates the dots from the scroll position. The nav hides entirely when
 * everything fits on one page.
 */
class DoseReviewCarousel extends HTMLElement {
  desktopQuery = window.matchMedia('(min-width: 750px)');
  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.track = this.querySelector('.dose-review-carousel__track');
    this.dotsContainer = this.querySelector('.dose-review-carousel__dots');
    this.nav = this.querySelector('.dose-review-carousel__nav');

    this.addEventListener('click', this.handleClick);
    this.track?.addEventListener('scroll', this.handleScroll, { passive: true });
    this.desktopQuery.addEventListener('change', this.handleBreakpointChange);

    this.buildDots();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.desktopQuery.removeEventListener('change', this.handleBreakpointChange);
    clearTimeout(this.scrollSettleTimer);
  }

  slides() {
    return [...this.querySelectorAll('.dose-review-carousel__slide')];
  }

  perView() {
    return this.desktopQuery.matches ? 3 : 1;
  }

  pageCount() {
    return Math.ceil(this.slides().length / this.perView());
  }

  /* One slide plus the flex gap — pages advance by perView of these */
  slotWidth() {
    const slide = this.slides()[0];
    if (!slide || !this.track) return 0;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.offsetWidth + gap;
  }

  currentPage() {
    if (!this.track) return 0;

    const pageWidth = this.slotWidth() * this.perView();
    if (!pageWidth) return 0;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll > 0 && this.track.scrollLeft >= maxScroll - 1) {
      return this.pageCount() - 1;
    }

    return Math.min(Math.round(this.track.scrollLeft / pageWidth), this.pageCount() - 1);
  }

  handleBreakpointChange = () => {
    this.buildDots();
    this.goToPage(0);
  };

  handleClick = (event) => {
    const arrow = event.target.closest('.dose-review-carousel__arrow');

    if (arrow && this.contains(arrow)) {
      const delta = arrow.dataset.direction === 'prev' ? -1 : 1;
      this.goToPage(this.currentPage() + delta);
      return;
    }

    const dot = event.target.closest('.dose-review-carousel__dot');

    if (dot && this.contains(dot)) {
      this.goToPage(parseInt(dot.dataset.page, 10));
    }
  };

  handleScroll = () => {
    clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = setTimeout(() => this.updateNav(), 80);
  };

  goToPage(page) {
    if (!this.track) return;

    const clamped = Math.max(0, Math.min(page, this.pageCount() - 1));
    const target = clamped * this.slotWidth() * this.perView();
    const behavior = this.reducedMotionQuery.matches ? 'auto' : 'smooth';

    this.track.scrollTo({ left: target, behavior });
  }

  buildDots() {
    if (!this.dotsContainer || !this.nav) return;

    this.dotsContainer.replaceChildren();

    const pages = this.pageCount();
    this.nav.hidden = pages <= 1;
    if (pages <= 1) return;

    for (let page = 0; page < pages; page += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dose-review-carousel__dot';
      dot.dataset.page = page;
      dot.setAttribute('aria-label', `Reviews page ${page + 1} of ${pages}`);
      this.dotsContainer.append(dot);
    }

    this.updateNav();
  }

  updateNav() {
    const page = this.currentPage();

    for (const dot of this.querySelectorAll('.dose-review-carousel__dot')) {
      dot.toggleAttribute('data-dash', parseInt(dot.dataset.page, 10) === page);
    }

    const prev = this.querySelector('[data-direction="prev"]');
    const next = this.querySelector('[data-direction="next"]');

    if (prev) prev.disabled = page <= 0;
    if (next) next.disabled = page >= this.pageCount() - 1;
  }
}

if (!customElements.get('dose-review-carousel')) {
  customElements.define('dose-review-carousel', DoseReviewCarousel);
}
